import React, { useState, useEffect, useRef, useMemo } from 'react';
import io from 'socket.io-client';
import appURLs from '../../appURL';
import getFetchConfig from '../../utils/getFetchConfig';
import ChatContent from './ChatContent/ChatContent';
import { useLocation } from 'react-router-dom';

function Chat({ selectedChat, newMessages, setnewMessages, listData, setSelectedChat, setdetailData }) {
  const ENDPOINT = 'https://menehariya-chat.netscapelabs.com/';
  
  // State
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [socket, setSocket] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false); // ✅ NEW: Track connection state
  const [chatDetailsData, setChatDetailsdata] = useState([]);
  const [loader, setloader] = useState(false);
  const [newMessageIds, setNewMessageIds] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [checkOnlinestatus, setcheckOnlineStatus] = useState(false);
  const [mylistData, setmylistData] = useState([]);
  const [myMessages, setMyMessages] = useState([]);
  
  const prevMessageCount = useRef(myMessages.length);
  const sendingRef = useRef(false);
  const socketRef = useRef(null); // ✅ NEW: Persistent socket reference

  const location = useLocation();
  const { message } = location.state || {};
  
  const appURL = appURLs();
  const fetchConfig = getFetchConfig();

  // Helper functions
  const checkAdsType = (adstype) => {
    if (adstype === 'Buy & Sell' || adstype === 'babysitter & nannie' || adstype === 'Local_biz & Service') {
      return adstype.replace(/ /g, "+").replace(/&/g, "%26")
    }
    return adstype;
  };

  const getIsoTime = () => new Date().toISOString();

  const containsUrl = (message) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return urlRegex.test(message);
  };

  // Get initial values from localStorage
  const adsId = localStorage.getItem("adsIdChat");
  const senderId = localStorage.getItem("userIdchat");
  const sellerId = localStorage.getItem("sellerIdChat");
  const ads_type = checkAdsType(localStorage.getItem("adsTypeChat"));
  const chat_id = localStorage.getItem("chatId");

  const decodedAdsType = ads_type ? decodeURIComponent(ads_type.replace(/\+/g, ' ')) : '';

  const [map, setMap] = useState({
    ads_id: adsId || '',
    sellerId: sellerId || '',
    senderId: JSON.parse(localStorage.getItem("user"))?.userId || "",
    buyerId: localStorage.getItem("buyerId") || '',
    ads_type: decodedAdsType,
    content: '',
    content_type: 'text'
  });

  // ✅ FIX 1: Initialize socket ONCE on component mount
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    const chatId = localStorage.getItem("chatId");

    if (!user) {
      console.warn("No user found in localStorage");
      return;
    }

    // Create socket connection
    const newSocket = io(ENDPOINT, {
      transports: ['websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      query: { userId: user?.userId }
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    // ✅ Connection handlers
    newSocket.on('connect', () => {
      console.log('✅ Socket connected:', newSocket.id);
      setSocketConnected(true);
      
      // Join room if chatId exists
      if (chatId) {
        newSocket.emit("join-room", chatId);
      }
    });

    newSocket.on('disconnect', () => {
      console.warn('⚠️ Socket disconnected');
      setSocketConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
      setSocketConnected(false);
    });

    // Cleanup
    return () => {
      console.log('Disconnecting socket...');
      newSocket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    };
  }, []); 

  
  useEffect(() => {
    if (!socketRef.current || !selectedChat) return;

    const socket = socketRef.current;
    const user = JSON.parse(localStorage.getItem('user'));
    const myUserId = user?.userId;

    // Join the selected chat room
    if (selectedChat._id) {
      socket.emit("join-room", selectedChat._id);
    }

    // User status handler
    const handleUserStatus = (data) => {
      if (data.userId.includes(map.sellerId)) {
        setcheckOnlineStatus(false);
      }
    };

    // Receive message handler
    const handleReceiveMessage = (data) => {
      // console.log("Chat Window Receive Message:", data);
      if (!data?._id || !data?.messages?.length) return;
      const incomingMessage = data.messages[0];
      // Only process if message belongs to open chat
      if (data._id !== selectedChat?._id) {
        return;
      }

      setMyMessages(prev => {
        // Strict duplicate check
        if (prev.some(msg => msg.key === incomingMessage._id)) {
          console.log("Duplicate message ID found, ignoring:", incomingMessage._id);
          return prev;
        }

        const isMe = (incomingMessage.senderId?._id === myUserId) || 
                     (incomingMessage.senderId === myUserId);

        // Optimistic deduplication for 'me'
        if (isMe) {
          const optimisticIndex = prev.findIndex(msg =>
            msg.user === 'me' &&
            msg.key.toString().startsWith('temp-') &&
            (msg.message === incomingMessage.content || 
             msg.message.trim() === incomingMessage.content.trim())
          );

          if (optimisticIndex !== -1) {
            const newMessages = [...prev];
            newMessages[optimisticIndex] = {
              ...newMessages[optimisticIndex],
              key: incomingMessage._id,
              timeStamp: incomingMessage.timestamp,
              type: incomingMessage.content_type || "text"
            };
            return newMessages;
          }
        }

        // Mark as read if from other
        if (!isMe) {
          socket.emit('is-read-message', {
            chatId: selectedChat._id,
            userId: myUserId,
            isread: "true",
          });
        }

        // Append new message
        return [
          ...prev,
          {
            message: incomingMessage.content,
            user: isMe ? "me" : "other",
            timeStamp: incomingMessage.timestamp,
            type: incomingMessage.content_type || "text",
            key: incomingMessage._id
          }
        ];
      });
    };

    // Delete message handlers
    const handleUpdateDeleteMsg = (data) => {
      setMyMessages(prev => prev.filter(msg => msg.key !== data?.messageId[0]));
    };

    const handleDeleteMsg = (deletedMessageId) => {
      setMessages(prev => prev.filter(msg => msg.id !== deletedMessageId));
    };

    // Register event listeners
    socket.on('user-status', handleUserStatus);
    socket.on('receive-message', handleReceiveMessage);
    socket.on('update-delete-msg', handleUpdateDeleteMsg);
    socket.on('delete-msg', handleDeleteMsg);

    // Cleanup listeners when chat changes
    return () => {
      socket.off('user-status', handleUserStatus);
      socket.off('receive-message', handleReceiveMessage);
      socket.off('update-delete-msg', handleUpdateDeleteMsg);
      socket.off('delete-msg', handleDeleteMsg);
    };
  }, [selectedChat, map.sellerId]);

  // Update map when selectedChat changes
  useEffect(() => {
    if (selectedChat) {
      setMap(prev => ({
        ...prev,
        ads_id: selectedChat.ads_id,
        sellerId: selectedChat.sellerId,
        buyerId: selectedChat.buyerId,
        ads_type: selectedChat.ads_type
      }));

      localStorage.setItem("adsIdChat", selectedChat.ads_id);
      localStorage.setItem("sellerIdChat", selectedChat.sellerId);
      localStorage.setItem("buyerId", selectedChat.buyerId);
      localStorage.setItem("adsTypeChat", selectedChat.ads_type);
      localStorage.setItem("chatId", selectedChat._id);
    }
  }, [selectedChat]);

  // File upload function
  const uploadFiles = async () => {
    setloader(true);
    setPreviewUrls([]);
    const fileUrls = [];

    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('photos', file);

        const token = localStorage.getItem("token");
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`${appURL}/chat/upload-file`, {
          method: 'POST',
          body: formData,
          headers: headers,
        });

        if (!response.ok) {
          throw new Error('File upload failed');
        }

        const data = await response.json();
        fileUrls.push(data.url);
        setloader(false);
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
    return fileUrls;
  };

  // Chat list data fetching
  const chatListData = async () => {
    if (!sellerId) {
      setChatDetailsdata(listData[0]);
      setSelectedChat(listData[0]);

      let newArray = [];
      listData?.map((item, index) => {
        newArray.push({
          message: item?.messages[0]?.content,
          timeStamp: item?.messages[0]?.timestamp,
          user: JSON.parse(localStorage.getItem("user"))?.userId === item?.messages[0]?.senderId ? "me" : "other",
          type: 'text',
          key: item?._id
        })
      })
      setMyMessages(Array.isArray(newArray) ? [...newArray].reverse() : []);
      return;
    }

    if (myMessages.length >= prevMessageCount.current) {
      setloader(true);
    }

    try {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(
        `${appURL}/chat/chat-details?ads_id=${adsId}&sellerId=${sellerId}&senderId=${senderId}&buyerId=${senderId}&ads_type=${ads_type}`,
        {
          ...fetchConfig,
          method: "GET",
          headers: headers,
        }
      );

      if (!response.ok) {
        setloader(false);
        throw new Error("Network response was not ok");
      }

      const jsonData = await response.json();
      
      if (jsonData.data?.status === 'active') {
        setcheckOnlineStatus(true);
      }
      
      setChatDetailsdata(jsonData.data);
      setloader(false);
      
      if (selectedChat === null) {
        setSelectedChat(jsonData.data);
      }

      let newMessagess = [];
      if (newMessages?.length === 0 || newMessages?.length === undefined) {
        jsonData?.data?.messages?.map((item, index) => {
          newMessagess.push({
            message: item?.content,
            timeStamp: item?.timestamp,
            user: JSON.parse(localStorage.getItem("user"))?.userId === item?.senderId?._id ? "me" : "other",
            type: 'text',
            key: item?._id
          })
        })
        setMyMessages(Array.isArray(newMessagess) ? [...newMessagess].reverse() : []);
      }
      
      setloader(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setloader(false);
    }
  };

  const derivedListData = useMemo(() => {
    return sellerId === null ? listData : null;
  }, [listData, sellerId]);

  useEffect(() => {
    chatListData();
  }, [selectedChat, derivedListData]);

  useEffect(() => {
    setmylistData(listData);
  }, [listData]);

  // ✅ FIX 3: Improved sendMessage with connection check
  const sendMessage = async () => {

    // ✅ Check if already sending
    if (sendingRef.current) {
      console.warn("Already sending, ignoring double trigger");
      return;
    }

    // ✅ Check socket connection
    if (!socketRef.current) {
      console.error("Socket instance not available");
      alert("Connection not established. Please refresh the page.");
      return;
    }

    if (!socketConnected) {
      console.error("Socket not connected");
      alert("You are not connected. Please wait for connection to establish.");
      return;
    }

    if (input.trim() === '' && selectedFiles.length === 0) return;

    sendingRef.current = true; // Lock

    const now = getIsoTime();

    const messageData = {
      ads_id: map.ads_id,
      sellerId: map.sellerId || selectedChat?.sellerId,
      senderId: map.senderId,
      buyerId: map.buyerId || selectedChat?.buyerId,
      ads_type: map.ads_type,
    };


    try {
      // ✅ Optimistic UI for text
      if (input.trim()) {
        const tempKey = `temp-${Date.now()}`;
        setMyMessages(prev => [
          ...prev,
          {
            message: input,
            user: "me",
            timeStamp: now,
            type: "text",
            key: tempKey
          }
        ]);

        socketRef.current.emit("send-message", {
          ...messageData,
          content: input,
          content_type: "text"
        });
      }

      // ✅ Handle files
      if (selectedFiles.length > 0) {
        const fileUrls = await uploadFiles();
        for (const url of fileUrls) {
          setMyMessages(prev => [
            ...prev,
            {
              message: url,
              user: "me",
              timeStamp: now,
              type: "file",
              key: `temp-file-${Date.now()}`
            }
          ]);

          socketRef.current.emit("send-message", {
            ...messageData,
            content: url,
            content_type: "file"
          });
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      // ✅ Clear input
      setInput('');
      setSelectedFiles([]);
      setPreviewUrls([]);

      // Unlock after short delay
      setTimeout(() => {
        sendingRef.current = false;
      }, 500);
    }
  };

  // Delete message
  const deleteMessage = (messageId) => {
    const deleteMap = {
      chatId: chatDetailsData._id,
      messageId: [messageId],
    };

    if (socketRef.current && socketConnected) {
      socketRef.current.emit('delete-msg', deleteMap);
      setMyMessages(prevMessages => prevMessages.filter(msg => msg.key !== messageId));
    }
  };

  // Delete chat
  const deleteChatUserSocket = () => {
    const deleteMap = {
      chatId: chatDetailsData._id,
    };
    
    if (socketRef.current && socketConnected) {
      socketRef.current.emit("delete-chat", deleteMap);
      chatListData();
    }
  };

  const handleFileSelect = (event) => {
    setSelectedFiles([...selectedFiles, ...event.target.files]);
  };

  localStorage.setItem('message_count', messages.length);

  return (
    <div>
      <ChatContent
        chatDetailsData={chatDetailsData}
        input={input}
        setInput={setInput}
        sendMessage={sendMessage}
        inputs={input}
        deleteMessage={deleteMessage}
        selectedChat={selectedChat}
        handleFileSelect={handleFileSelect}
        checkOnlinestatus={checkOnlinestatus}
        ads_type={ads_type}
        adsId={adsId}
        setSelectedFiles={setSelectedFiles}
        selectedFiles={selectedFiles}
        myMessages={myMessages}
        loader={loader}
        setPreviewUrls={setPreviewUrls}
        previewUrls={previewUrls}
        listData={listData}
        userData={message}
        socketConnected={socketConnected} // ✅ Pass connection status to child
      />
    </div>
  );
}

export default Chat;